import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@carbon/react";
import { Checkmark, Close, View, WarningAltFilled } from "@carbon/icons-react";
import DataTable, {
  DataTableColumn,
  DataTableRow,
} from "../../../components/carbon/DataTable/DataTable";
import EmptyState from "../../../components/carbon/EmptyState/EmptyState";
import PageHeader from "../../../components/carbon/PageHeader/PageHeader";
import { useNotification } from "../../../context";
import { isOcConflict } from "../../../hooks/useOcActions";
import { isOcNotFound, useRespondOc } from "../../../hooks/useRespondOc";
import { useSupplierOcs } from "../../../hooks/useSupplierOcs";
import { PurchaseOrderSellerSummary } from "../../../types";
import { formatDate } from "../../../utils/utils";
import OcStatusPill from "../components/OcStatusPill";
import { isOcOverdue } from "../ocOverdue";
import { formatOrderTotal } from "../formatOrderTotal";
import RejectSupplierOcModal from "./RejectSupplierOcModal";
import SupplierOcDetailModal from "./SupplierOcDetailModal";
import "../purchaseOrders.scss";
import "./supplierOc.scss";

interface SupplierOcRow extends DataTableRow {
  code: string;
  buyer: string;
  total: string;
  requestedDelivery: React.ReactNode;
  status: React.ReactNode;
  actions: React.ReactNode;
}

/** The seller can answer only while the OC is still SENT (and the server says it is actionable). */
const isActionable = (oc: PurchaseOrderSellerSummary): boolean =>
  oc.status === "SENT" && oc.actionable !== false;

/**
 * Seller-side OC portal: the OCs addressed to the caller's company with Accept / Reject on the
 * ones still awaiting an answer. Each row formats money with its OWN currency (never aggregated).
 * Reject goes through a dialog that requires a reason; a 404 (not this company's OC) or 409
 * (already responded) is an error toast and the list is refreshed, never a crash.
 */
const SupplierPurchaseOrders = () => {
  const { t } = useTranslation();
  const { openNotification } = useNotification();
  const { data, isLoading } = useSupplierOcs();
  const respond = useRespondOc();
  const [rejecting, setRejecting] = useState<PurchaseOrderSellerSummary | null>(
    null
  );
  const [viewing, setViewing] = useState<PurchaseOrderSellerSummary | null>(
    null
  );

  const onError = (error: unknown) =>
    openNotification(
      t(
        isOcNotFound(error)
          ? "purchaseOrders.supplier.notFound"
          : isOcConflict(error)
            ? "purchaseOrders.supplier.alreadyResponded"
            : "purchaseOrders.supplier.error"
      )
    );

  const accept = (oc: PurchaseOrderSellerSummary) =>
    respond.mutate(
      { id: oc.id, accept: true },
      {
        onSuccess: () =>
          openNotification(t("purchaseOrders.supplier.acceptSuccess")),
        onError,
      }
    );

  const reject = (oc: PurchaseOrderSellerSummary, reason: string) =>
    respond.mutate(
      { id: oc.id, accept: false, reason },
      {
        onSuccess: () => {
          openNotification(t("purchaseOrders.supplier.rejectSuccess"));
          setRejecting(null);
        },
        onError: (error) => {
          onError(error);
          // A 404/409 makes the dialog moot (the list refetches); other errors keep it open to retry.
          if (isOcNotFound(error) || isOcConflict(error)) setRejecting(null);
        },
      }
    );

  const rows: SupplierOcRow[] = (data ?? []).map((oc) => ({
    id: oc.id,
    code: oc.code,
    buyer: oc.buyerCompanyName ?? "-",
    total: formatOrderTotal(oc.totalAmount, oc.currency),
    requestedDelivery: oc.requestedDeliveryDate ? (
      <div className="purchase-orders__delivery">
        <span>{formatDate(oc.requestedDeliveryDate)}</span>
        {isOcOverdue(oc) ? (
          <span className="purchase-orders__overdue">
            <WarningAltFilled />
            {t("purchaseOrders.supplier.overdue")}
          </span>
        ) : null}
      </div>
    ) : (
      "-"
    ),
    status: <OcStatusPill status={oc.status} />,
    actions: (
      <div className="purchase-orders__row-actions">
        <IconButton
          kind="ghost"
          size="sm"
          align="left"
          label={t("purchaseOrders.supplier.view")}
          onClick={() => setViewing(oc)}
        >
          <View />
        </IconButton>
        {isActionable(oc) ? (
          <>
            <IconButton
              kind="ghost"
              size="sm"
              align="left"
              label={t("purchaseOrders.supplier.accept")}
              disabled={respond.isPending}
              onClick={() => accept(oc)}
            >
              <Checkmark />
            </IconButton>
            <IconButton
              kind="ghost"
              size="sm"
              align="left"
              label={t("purchaseOrders.supplier.reject.action")}
              disabled={respond.isPending}
              onClick={() => setRejecting(oc)}
            >
              <Close />
            </IconButton>
          </>
        ) : null}
      </div>
    ),
  }));

  const headers: DataTableColumn<SupplierOcRow>[] = [
    { key: "code", header: t("purchaseOrders.supplier.code") },
    { key: "buyer", header: t("purchaseOrders.supplier.buyer") },
    { key: "total", header: t("purchaseOrders.supplier.total") },
    {
      key: "requestedDelivery",
      header: t("purchaseOrders.supplier.requestedDelivery"),
    },
    { key: "status", header: t("purchaseOrders.supplier.statusColumn") },
    { key: "actions", header: t("purchaseOrders.supplier.actionsColumn") },
  ];

  return (
    <div>
      <PageHeader title={t("purchaseOrders.supplier.title")} />
      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        empty={
          <EmptyState
            title={t("purchaseOrders.supplier.empty")}
            description={t("purchaseOrders.supplier.emptyDescription")}
          />
        }
      />
      <RejectSupplierOcModal
        open={rejecting !== null}
        code={rejecting?.code ?? ""}
        isPending={respond.isPending}
        onClose={() => setRejecting(null)}
        onSubmit={(reason) => rejecting && reject(rejecting, reason)}
      />
      <SupplierOcDetailModal oc={viewing} onClose={() => setViewing(null)} />
    </div>
  );
};

export default SupplierPurchaseOrders;
